<template>
  <label
    class="play-radio"
    :class="[`play-radio--${variant}`, { 'is-disabled': disabled }]"
  >
    <input
      class="play-radio__native"
      type="radio"
      :name="name"
      :checked="isChecked"
      :disabled="disabled"
      v-bind="$attrs"
      @change="$emit('change', value)"
    >
    <span
      class="play-radio__dot"
      aria-hidden="true"
    />
    <span
      v-if="$slots.default || label"
      class="play-radio__label"
    ><slot>{{ label }}</slot></span>
  </label>
</template>

<script>
/**
 * PlayRadio — a single radio control for the Play system.
 *
 * Token-driven (see src/styles/tokens.scss): no hardcoded colors. Replaces a
 * single bootstrap-vue <b-form-radio>. A visually-hidden native <input
 * type="radio"> drives real keyboard + form semantics (arrow-key navigation
 * within a `name` group); the dot is the styled visual.
 *
 * Usually composed by <PlayRadioGroup>, but works standalone: bind `checked`
 * (the group's selected value), set this radio's `value`, share a `name`.
 *
 * VUE3-MIGRATION: `model: { prop: 'checked', event: 'change' }` — Vue 3 uses
 * `modelValue` / `update:modelValue`. Change this block + the emit name on
 * migration. $attrs (id/…) already fall through to the native input.
 */
export default {
  name: 'PlayRadio',
  inheritAttrs: false,
  model: {
    prop: 'checked',
    event: 'change',
  },
  props: {
    /** This radio's value */
    value: {
      type: [String, Number, Boolean],
      default: '',
    },
    /** The group's currently-selected value (v-model target) */
    checked: {
      type: [String, Number, Boolean],
      default: '',
    },
    /** Shared group name for native radio semantics */
    name: {
      type: String,
      default: '',
    },
    /** Convenience label when no default slot is given */
    label: {
      type: String,
      default: '',
    },
    variant: {
      type: String,
      default: 'primary',
      validator: v => ['primary', 'secondary', 'highlight', 'accent'].includes(v),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isChecked() {
      return this.checked === this.value
    },
  },
}
</script>

<style lang="scss" scoped>
.play-radio {
  --rd-on: var(--c-primary);
  --rd-on-strong: var(--c-primary-strong);
  display: inline-flex;
  align-items: center;
  gap: 11px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &--secondary { --rd-on: var(--c-secondary); --rd-on-strong: var(--c-secondary-strong); }
  &--highlight { --rd-on: var(--c-highlight); --rd-on-strong: var(--c-highlight-strong); }
  &--accent    { --rd-on: var(--c-accent);    --rd-on-strong: var(--c-accent-strong); }

  &__native {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    margin: 0;
    pointer-events: none;
  }

  &__dot {
    flex-shrink: 0;
    position: relative;
    width: 24px;
    height: 24px;
    background: var(--surface-card);
    border: var(--border-w) solid var(--border-ink);
    border-radius: 50%;
    box-shadow: 0 3px 0 var(--border-ink);
    transition:
      transform var(--dur-fast) var(--ease-bounce),
      box-shadow var(--dur-fast) var(--ease-out);

    &::after {
      content: "";
      position: absolute;
      inset: 0;
      margin: auto;
      width: 11px;
      height: 11px;
      border-radius: 50%;
      background: var(--rd-on);
      transform: scale(0);
      transition: transform var(--dur) var(--ease-bounce);
    }
  }

  &__label {
    font-family: var(--font-body);
    font-weight: 500;
    color: var(--text-body);
    line-height: 1.45;
  }

  /* ── Checked ── */
  &__native:checked ~ .play-radio__dot {
    border-color: var(--rd-on-strong);
    box-shadow: 0 3px 0 var(--rd-on-strong);

    &::after { transform: scale(1); }
  }

  /* ── Hover / active / focus ── */
  &:hover .play-radio__dot { box-shadow: 0 4px 0 var(--border-ink); }
  &:active .play-radio__dot { transform: translateY(2px); box-shadow: var(--shadow-press); }

  &__native:focus-visible ~ .play-radio__dot {
    outline: 2px solid var(--c-secondary);
    outline-offset: 2px;
  }

  /* ── Disabled ── */
  &.is-disabled {
    cursor: not-allowed;
    opacity: .55;
  }
}

@media (prefers-reduced-motion: reduce) {
  .play-radio__dot,
  .play-radio__dot::after { transition: none; }
}
</style>
